import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CatalogDataSource,
  ReportConfig,
  ReportRunResult,
  SavedReport,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useReportCatalog(companyId: string | null) {
  return useQuery({
    queryKey: ['report-catalog', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ dataSources: CatalogDataSource[] }>(`/companies/${companyId}/report-builder/catalog`).then((r) => r.dataSources),
  });
}

export function useSavedReports(companyId: string | null) {
  return useQuery({
    queryKey: ['saved-reports', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ reports: SavedReport[] }>(`/companies/${companyId}/report-builder/saved`).then((r) => r.reports),
  });
}

export function useRunReport(companyId: string | null) {
  return useMutation({
    mutationFn: (config: ReportConfig) => {
      if (!companyId) throw new Error('No active company');
      return api.post<ReportRunResult>(`/companies/${companyId}/report-builder/run`, { config });
    },
  });
}

function invalidateSaved(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  qc.invalidateQueries({ queryKey: ['saved-reports', companyId ?? 'none'] });
}

export function useSaveReport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, config }: { name: string; config: ReportConfig }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ report: SavedReport }>(`/companies/${companyId}/report-builder/saved`, { name, config }).then((r) => r.report);
    },
    onSuccess: () => invalidateSaved(qc, companyId),
  });
}

export function useDeleteSavedReport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<void>(`/companies/${companyId}/report-builder/saved/${id}`);
    },
    onSuccess: () => invalidateSaved(qc, companyId),
  });
}
